import { ArrowDownUp } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { type FieldArrayWithId, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { Button } from "~/components/ui";
import type { PropertySchemaAssociationsFormDataBase } from "~/features/admin/propertySchemas/types";
import { cn } from "~/lib/utils";
import { PropertySchemaAssociationFormInput } from "./PropertySchemaAssociationFormInput";

interface PropertySchemaAssociationsListProps {
  fields: FieldArrayWithId<PropertySchemaAssociationsFormDataBase, "propertySchemaAssociations">[];
  onRemove: (index: number) => void;
  onMove: (fromIndex: number, toIndex: number) => void;
  showPropertyAttributeTypeId?: boolean;
  supportedPropertyTypes?: string[];
  title?: React.ReactNode;
}

/**
 * Renders the array of property-schema rows with a built-in sort mode that
 * supports both HTML5 drag-and-drop (drag handle on each card) and explicit
 * Up / Down buttons. Position values are synced to array indices on every
 * change so the saved order matches what the user sees.
 */
export function PropertySchemaAssociationsList({
  fields,
  onRemove,
  onMove,
  showPropertyAttributeTypeId,
  supportedPropertyTypes,
  title,
}: PropertySchemaAssociationsListProps) {
  const { t } = useTranslation();
  const { setValue } = useFormContext<PropertySchemaAssociationsFormDataBase>();
  const [sortMode, setSortMode] = useState(false);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

  // Keep the position values aligned with the array index.
  useEffect(() => {
    fields.forEach((_, i) => {
      setValue(`propertySchemaAssociations.${i}.position`, i, { shouldDirty: false });
    });
  }, [fields, setValue]);

  const handleMoveUp = useCallback(
    (index: number) => {
      if (index > 0) onMove(index, index - 1);
    },
    [onMove],
  );

  const handleMoveDown = useCallback(
    (index: number) => {
      if (index < fields.length - 1) onMove(index, index + 1);
    },
    [onMove, fields.length],
  );

  const handleDragStart = useCallback((e: React.DragEvent, index: number) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = "move";
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent, index: number) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setDragOverIndex(index);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    const related = e.relatedTarget as Node | null;
    if (related && e.currentTarget.contains(related)) return;
    setDragOverIndex(null);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent, dropIndex: number) => {
      e.preventDefault();
      if (draggedIndex === null || draggedIndex === dropIndex) {
        setDraggedIndex(null);
        setDragOverIndex(null);
        return;
      }
      onMove(draggedIndex, dropIndex);
      setDraggedIndex(null);
      setDragOverIndex(null);
    },
    [draggedIndex, onMove],
  );

  const handleDragEnd = useCallback(() => {
    setDraggedIndex(null);
    setDragOverIndex(null);
  }, []);

  return (
    <div className="space-y-3">
      {(title || fields.length > 1) && (
        <div className="flex items-center justify-between">
          {title}
          {fields.length > 1 && (
            <Button
              type="button"
              variant={sortMode ? "default" : "outline"}
              size="sm"
              onClick={() => setSortMode((prev) => !prev)}
            >
              <ArrowDownUp />
              <span>{t("ui.sort")}</span>
            </Button>
          )}
        </div>
      )}
      {fields.map((field, index) => (
        // biome-ignore lint/a11y/noStaticElementInteractions: drag-and-drop reorder requires drag handlers on the row wrapper; explicit Up/Down buttons inside the card provide the keyboard-accessible path
        <div
          key={field.id}
          draggable={sortMode}
          onDragStart={sortMode ? (e) => handleDragStart(e, index) : undefined}
          onDragOver={sortMode ? (e) => handleDragOver(e, index) : undefined}
          onDragLeave={sortMode ? handleDragLeave : undefined}
          onDrop={sortMode ? (e) => handleDrop(e, index) : undefined}
          onDragEnd={sortMode ? handleDragEnd : undefined}
          className={cn(
            "border-t-2 border-transparent transition-opacity",
            sortMode && "select-none",
            draggedIndex === index && "opacity-50",
            dragOverIndex === index && "border-primary-500",
          )}
        >
          <PropertySchemaAssociationFormInput
            index={index}
            onRemove={onRemove}
            showPropertyAttributeTypeId={showPropertyAttributeTypeId}
            supportedPropertyTypes={supportedPropertyTypes}
            sortMode={sortMode}
            onMoveUp={() => handleMoveUp(index)}
            onMoveDown={() => handleMoveDown(index)}
            isFirst={index === 0}
            isLast={index === fields.length - 1}
          />
        </div>
      ))}
    </div>
  );
}
